import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hasPermission, PERMISSIONS } from '@helios/authz';
import { useAuth } from '@/features/auth/authContext';
import { useSessionScopeFence } from '@/hooks/useSessionScope';
import type { RequestOptions } from '@/services/apiClient';
import { isSocketReady, releaseSocket, retainSocket } from '@/services/wsClient';
import type { RealtimeEventItem } from '@/services/realtimeClient';
import { projectsClient } from './projects.api';
import type {
  ProjectActionInput,
  ProjectFilters,
  ProjectIdentity,
  ProjectInput,
  ProjectPortfolioFilters,
  UpdateProjectInput,
} from './projects.types';

const SCOPE_FILTERS: ProjectFilters = {
  search: '',
  status: '',
  phase: '',
  riskLevel: '',
  sector: '',
  projectId: '',
  regionId: '',
  page: 1,
  pageSize: 100,
  sortBy: 'name',
  sortOrder: 'asc',
};

export const projectKeys = {
  root: (identity: ProjectIdentity) =>
    [
      'projects',
      'tenant',
      identity.tenantId,
      'user',
      identity.userId,
      'grants',
      identity.permissions ?? '',
      'session',
      identity.sessionOrigin ?? null,
    ] as const,
  lists: (identity: ProjectIdentity) => [...projectKeys.root(identity), 'list'] as const,
  list: (identity: ProjectIdentity, filters: ProjectFilters) =>
    [...projectKeys.lists(identity), filters] as const,
  scopes: (identity: ProjectIdentity) => [...projectKeys.root(identity), 'scopes'] as const,
  summary: (identity: ProjectIdentity, filters: ProjectPortfolioFilters) =>
    [...projectKeys.root(identity), 'summary', filters] as const,
  detail: (identity: ProjectIdentity, id: string) =>
    [...projectKeys.root(identity), 'detail', id] as const,
  timeline: (identity: ProjectIdentity, id: string) =>
    [...projectKeys.detail(identity, id), 'timeline'] as const,
};

export function useProjectIdentity() {
  const auth = useAuth();
  const { identity: scopeIdentity, enabled, isCurrent: scopeIsCurrent } = useSessionScopeFence();
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const isCurrent = useCallback(
    (origin: typeof scopeIdentity) => mounted.current && scopeIsCurrent(origin),
    [scopeIsCurrent],
  );
  const { tenantId, userId, permissions, sessionOrigin } = scopeIdentity;
  const identity = useMemo(
    () => ({ tenantId: tenantId ?? '', userId: userId ?? '', permissions, sessionOrigin }),
    [tenantId, userId, permissions, sessionOrigin],
  );
  return {
    auth,
    identity,
    scopeIdentity,
    isCurrent,
    enabled,
  };
}

function useProjectQuery<T>(
  key: (identity: ProjectIdentity) => readonly unknown[],
  loader: (options: RequestOptions) => Promise<T>,
  active = true,
  staleTime = 15_000,
  refetchInterval?: number,
) {
  const { identity, scopeIdentity, auth, enabled: actorEnabled, isCurrent } = useProjectIdentity();
  const enabled =
    actorEnabled && active && hasPermission(auth.permissions, PERMISSIONS.PROJECTS_READ);
  const query = useQuery({
    queryKey: key(identity),
    queryFn: async ({ signal }) => {
      if (!enabled || !isCurrent(scopeIdentity))
        throw new Error('Current project access is required.');
      const result = await loader({ signal, sessionOrigin: scopeIdentity.sessionOrigin });
      signal.throwIfAborted();
      if (!isCurrent(scopeIdentity)) throw new Error('Project access changed while loading.');
      return result;
    },
    enabled,
    placeholderData: () => undefined,
    retry: false,
    staleTime,
    refetchInterval,
  });
  return { ...query, data: enabled && !query.isError ? query.data : undefined };
}

export function useProjects(filters: ProjectFilters, active = true) {
  return useProjectQuery(
    (identity) => projectKeys.list(identity, filters),
    (options) => projectsClient.list(filters, options),
    active,
  );
}

export function useProjectPortfolioSummary(filters: ProjectPortfolioFilters) {
  return useProjectQuery(
    (identity) => projectKeys.summary(identity, filters),
    (options) => projectsClient.portfolioSummary(filters, options),
  );
}

export function useProjectPortfolioEvidence(filters: ProjectPortfolioFilters) {
  return useProjectQuery(
    (identity) => [...projectKeys.root(identity), 'evidence', filters],
    (options) => projectsClient.portfolioEvidence(filters, options),
    true,
    0,
    30_000,
  );
}

export function useProjectPortfolioMap(filters: ProjectFilters) {
  const membership = {
    ...filters,
    page: 1,
    pageSize: 100,
    sortBy: 'name' as const,
    sortOrder: 'asc' as const,
  };
  return useProjectQuery(
    (identity) => [...projectKeys.root(identity), 'portfolio-map', membership],
    (options) => projectsClient.portfolioMap(membership, options),
  );
}

export function useProjectScopeOptions() {
  return useProjectQuery(
    projectKeys.scopes,
    (options) => projectsClient.listAllScopes(SCOPE_FILTERS, options),
    true,
    60_000,
  );
}

export function useProjectDetail(id: string) {
  return useProjectQuery(
    (identity) => projectKeys.detail(identity, id),
    (options) => projectsClient.get(id, options),
    Boolean(id),
  );
}

export function useProjectTimeline(id: string) {
  return useProjectQuery(
    (identity) => projectKeys.timeline(identity, id),
    (options) => projectsClient.timeline(id, options),
    Boolean(id),
  );
}

export function useProjectMutations() {
  const queryClient = useQueryClient();
  const { identity, scopeIdentity, auth, enabled, isCurrent } = useProjectIdentity();
  const guarded = async <T>(
    permission: string,
    action: (options: RequestOptions) => Promise<T>,
  ) => {
    if (!enabled || !isCurrent(scopeIdentity) || !hasPermission(auth.permissions, permission))
      throw new Error('Current project permission is required.');
    const result = await action({ sessionOrigin: scopeIdentity.sessionOrigin });
    if (!isCurrent(scopeIdentity))
      throw new Error('Project access changed while the action was completing.');
    return result;
  };
  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: projectKeys.root(identity) }),
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'command-center'] }),
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'realtime',
      }),
    ]);
  };

  return {
    create: useMutation({
      mutationFn: (input: ProjectInput) =>
        guarded(PERMISSIONS.PROJECTS_CREATE, (options) => projectsClient.create(input, options)),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, input }: { id: string; input: UpdateProjectInput }) =>
        guarded(PERMISSIONS.PROJECTS_UPDATE, (options) =>
          projectsClient.update(id, input, options),
        ),
      onSuccess: invalidate,
    }),
    archive: useMutation({
      mutationFn: ({ id, version }: { id: string; version: number }) =>
        guarded(PERMISSIONS.PROJECTS_DELETE, (options) =>
          projectsClient.archive(id, version, options),
        ),
      onSuccess: invalidate,
    }),
    action: useMutation({
      mutationFn: ({ id, input }: { id: string; input: ProjectActionInput }) =>
        guarded(PERMISSIONS.PROJECTS_EXECUTE, (options) =>
          projectsClient.runAction(id, input, options),
        ),
      onSuccess: invalidate,
    }),
  };
}

export type ProjectSocketStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

export function useProjectsRealtime(): ProjectSocketStatus {
  const queryClient = useQueryClient();
  const { identity, scopeIdentity, enabled: actorEnabled, auth, isCurrent } = useProjectIdentity();
  const enabled = actorEnabled && hasPermission(auth.permissions, PERMISSIONS.PROJECTS_READ);
  const [status, setStatus] = useState<ProjectSocketStatus>(
    enabled ? 'connecting' : 'disconnected',
  );

  useEffect(() => {
    if (!enabled) {
      setStatus('disconnected');
      return;
    }

    setStatus('connecting');
    const socket = retainSocket();
    const refresh = () => {
      if (!isCurrent(scopeIdentity)) return;
      void queryClient.invalidateQueries({ queryKey: projectKeys.root(identity) });
      void queryClient.invalidateQueries({ queryKey: ['dashboard', 'command-center'] });
      void queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'realtime',
      });
    };
    const onTransportConnect = () => setStatus('connecting');
    const onReady = () => {
      if (!isCurrent(scopeIdentity)) return;
      setStatus('connected');
      socket.emit('realtime.subscribe', { topic: 'projects.projects' });
      refresh();
    };
    const onEvent = (event: RealtimeEventItem) => {
      if (
        event?.topic === 'projects.projects' &&
        event.tenantId === identity.tenantId &&
        (event.recipientUserId === null || event.recipientUserId === identity.userId)
      ) {
        refresh();
      }
    };
    const onDisconnect = () => setStatus('disconnected');
    const onError = () => setStatus('error');

    socket.on('connect', onTransportConnect);
    socket.on('realtime.connected', onReady);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onError);
    socket.on('realtime.error', onError);
    socket.on('realtime.event', onEvent);
    if (isSocketReady()) onReady();

    return () => {
      socket.off('connect', onTransportConnect);
      socket.off('realtime.connected', onReady);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onError);
      socket.off('realtime.error', onError);
      socket.off('realtime.event', onEvent);
      releaseSocket();
    };
  }, [enabled, identity, scopeIdentity, isCurrent, queryClient]);

  return status;
}
